import { t } from '@/utils/i18n'
import { Container } from '@/components/ui/Container'
import { createIcon } from '@/utils/icons'

export function ProblemSolutionSection(): HTMLElement {
  const section = document.createElement('section')
  section.className = 'section-container bg-white'

  const container = Container({
    children: [
      createSectionHeader(),
      createProblemSolutionGrid(),
    ],
  })

  section.appendChild(container)
  return section
}

function createSectionHeader(): HTMLElement {
  const header = document.createElement('div')
  header.className = 'text-center mb-16'

  const title = document.createElement('h2')
  title.textContent = t('problemSolution.sectionTitle')
  title.className = 'text-4xl sm:text-5xl font-bold text-slate-900 mb-4'

  header.appendChild(title)
  return header
}

function createProblemSolutionGrid(): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'space-y-8'

  // Headers row
  const headersRow = document.createElement('div')
  headersRow.className = 'grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8'

  const problemHeader = createProblemHeader()
  const solutionHeader = createSolutionHeader()

  headersRow.appendChild(problemHeader)
  headersRow.appendChild(solutionHeader)
  wrapper.appendChild(headersRow)

  // Problem-Solution pairs
  const problems = [
    {
      iconPath: 'M12 2v20M2 12h20',
      title: t('problemSolution.problem.point1Title'),
      text: t('problemSolution.problem.point1Text'),
    },
    {
      iconPath: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
      title: t('problemSolution.problem.point2Title'),
      text: t('problemSolution.problem.point2Text'),
    },
    {
      iconPath: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
      title: t('problemSolution.problem.point3Title'),
      text: t('problemSolution.problem.point3Text'),
    },
    {
      iconPath: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
      title: t('problemSolution.problem.point4Title'),
      text: t('problemSolution.problem.point4Text'),
    },
  ]

  const solutions = [
    {
      title: t('problemSolution.solution.point1Title'),
      text: t('problemSolution.solution.point1Text'),
    },
    {
      title: t('problemSolution.solution.point2Title'),
      text: t('problemSolution.solution.point2Text'),
    },
    {
      title: t('problemSolution.solution.point3Title'),
      text: t('problemSolution.solution.point3Text'),
    },
    {
      title: t('problemSolution.solution.point4Title'),
      text: t('problemSolution.solution.point4Text'),
    },
  ]

  const checkIconPath = 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3'

  problems.forEach((problem, index) => {
    const row = document.createElement('div')
    row.className = 'grid grid-cols-1 lg:grid-cols-2 gap-12'

    // Problem side
    const problemPoint = createProblemPoint(problem)

    // Solution side
    const solutionPoint = createSolutionPoint(solutions[index], checkIconPath)

    row.appendChild(problemPoint)
    row.appendChild(solutionPoint)
    wrapper.appendChild(row)
  })

  return wrapper
}

function createProblemHeader(): HTMLElement {
  const header = document.createElement('div')

  const headline = document.createElement('h3')
  headline.textContent = t('problemSolution.problem.headline')
  headline.className = 'text-2xl sm:text-3xl font-bold text-slate-900 mb-4'

  const intro = document.createElement('p')
  intro.textContent = t('problemSolution.problem.intro')
  intro.className = 'text-slate-600 leading-relaxed'

  header.appendChild(headline)
  header.appendChild(intro)

  return header
}

function createSolutionHeader(): HTMLElement {
  const header = document.createElement('div')

  const headline = document.createElement('h3')
  headline.textContent = t('problemSolution.solution.headline')
  headline.className = 'text-2xl sm:text-3xl font-bold text-slate-900 mb-4'

  const intro = document.createElement('p')
  intro.textContent = t('problemSolution.solution.intro')
  intro.className = 'text-slate-600 leading-relaxed'

  header.appendChild(headline)
  header.appendChild(intro)

  return header
}

function createProblemPoint(problem: { iconPath: string; title: string; text: string }): HTMLElement {
  const point = document.createElement('div')
  point.className = 'flex gap-4'

  const iconContainer = document.createElement('div')
  iconContainer.className = 'flex-shrink-0 w-12 h-12 rounded-lg bg-red-100 text-red-600 flex items-center justify-center p-2.5'
  iconContainer.appendChild(createIcon(problem.iconPath))

  const content = document.createElement('div')
  content.className = 'flex-1'

  const title = document.createElement('h4')
  title.textContent = problem.title
  title.className = 'text-lg font-bold text-slate-900 mb-1'

  const text = document.createElement('p')
  text.textContent = problem.text
  text.className = 'text-slate-600 text-sm leading-relaxed'

  content.appendChild(title)
  content.appendChild(text)
  point.appendChild(iconContainer)
  point.appendChild(content)

  return point
}

function createSolutionPoint(solution: { title: string; text: string }, iconPath: string): HTMLElement {
  const point = document.createElement('div')
  point.className = 'flex gap-4'

  const iconContainer = document.createElement('div')
  iconContainer.className = 'flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center p-2.5'
  iconContainer.appendChild(createIcon(iconPath))

  const content = document.createElement('div')
  content.className = 'flex-1'

  const title = document.createElement('h4')
  title.textContent = solution.title
  title.className = 'text-lg font-bold text-slate-900 mb-1'

  const text = document.createElement('p')
  text.textContent = solution.text
  text.className = 'text-slate-600 text-sm leading-relaxed'

  content.appendChild(title)
  content.appendChild(text)
  point.appendChild(iconContainer)
  point.appendChild(content)

  return point
}
